import {
  isDefinedArray,
  isDefinedString,
  isNullOrUndefined,
  isPositiveNumber,
  reduce
} from "et3-type-guards";
import { memoize, orderBy } from "lodash";
import { computed, observable, runInAction } from "mobx";
import queryString from "query-string";
import { getSpecificDateWithDays } from "../../core/et3-date";
import {
  TCorePage,
  TPendingTReports,
  TReport,
  TStatusExtraData
} from "../../Types";
import getCleanPoolStore from "../core/core-clean-pool";
import getCoreHistoryStore from "../core/core-history-store";
import getDashBoardStore from "../dashboard-store";
import {
  E_SESSION_STORAGE_KEY,
  E_STORAGE_TYPE
} from "../../constants/storage-constants";
import getCoreStorageStore from "../core/core-storage-store";
import banReportOnDatabase from "./ban-report";
import getReportsFromDatabase, {
  getReportByIDFromDatabase,
  getReportByUserIDFromDatabase
} from "./get-reports";
import resolveReportOnDatabase from "./resolve-report";
import warnReportOnDatabase from "./warn-report";
import { TEt3PickerSingleValue } from "../../TypesNEW";

export type test = [
  string,
  {
    data: TReport[];
    total: number;
  }
];
class ReportsStore {
  fromDate = observable.box<Date>(
    getSpecificDateWithDays({
      daysSubtracted: 5,
      daysAdded: 0
    })
  );
  toDate = observable.box<Date>(
    getSpecificDateWithDays({ daysSubtracted: 0, daysAdded: 1 })
  );
  status = observable.box<TEt3PickerSingleValue>({
    label: "Pending",
    value: "pending"
  });
  createdFromType = observable.box<TEt3PickerSingleValue>({
    label: "Other",
    value: "other"
  });
  pendingReports = observable.box<TPendingTReports>({});
  reportsByTypeLimit = observable.box<TEt3PickerSingleValue>({
    label: "5",
    value: "5"
  });
  reportsByType = observable.box<TReport[]>([]);
  selectedReasons = observable.box([]);
  order = observable.box<TEt3PickerSingleValue>({
    label: "ASC",
    value: "ASC"
  });

  orderedPendingReports = computed(() => {
    const reportsToPairArray = Object.entries(this.pendingReports.get());
    const orderedPendingReportsByTotalAndTime = orderBy(
      reportsToPairArray,
      [(pair) => pair[1].total, (pair) => pair[1].data[0].reportedOn],
      ["desc", "asc"]
    );
    return orderedPendingReportsByTotalAndTime;
  });

  isDefinedPendingReportsData = computed(() => {
    const pendingReports = this.pendingReports.get();
    const reportedIds = Object.keys(pendingReports);
    const isDefinedPendingReportsData = isDefinedArray(reportedIds);
    return isDefinedPendingReportsData;
  });

  isDefinedReportsByTypeData = computed(() => {
    const reportsByType = this.reportsByType.get();
    const definedReportsByTypeData = isDefinedArray(reportsByType);
    return definedReportsByTypeData;
  });

  setOrder = (order: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.order.set(order);
    });
  };

  setPendingReports = (reports: TPendingTReports) => {
    runInAction(() => {
      this.pendingReports.set(reports);
    });
  };

  setReportsByType = (reports: TReport[]) => {
    runInAction(() => {
      this.reportsByType.set(reports);
    });
  };

  setReportsByTypeLimit = (limit: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.reportsByTypeLimit.set(limit);
    });
  };

  setReportStatus = (status: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.status.set(status);
    });
  };

  setReportCreatedFromType = (status: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.createdFromType.set(status);
    });
  };

  cleanReports = () => {
    runInAction(() => {
      this.setPendingReports({});
      this.setReportsByType([]);
    });
  };

  getReports = async (withURLSearch: boolean) => {
    await getReportsFromDatabase(withURLSearch);
  };

  getReportByID = async (id: number) => {
    await getReportByIDFromDatabase(id);
  };

  getReportByUserID = async (userID: number) => {
    await getReportByUserIDFromDatabase(userID);
  };

  getReportReasons = (reasons: number[]) => {
    let reportReasons = "";
    reasons.forEach((reason, index) => {
      if (reason === 1) {
        reportReasons += `REPORT_REASON_${index + 1}` + ", ";
      }
    });
    return reportReasons.slice(-2);
  };

  goToViewConversationNewTab = ({
    senderID,
    recipientID
  }: {
    senderID: number;
    recipientID: number;
  }) => {
    getCoreHistoryStore().openNewTab(
      `/users/conversations?senderID=${senderID}&recipientID=${recipientID}`
    );
  };
  removePendingReport = ({
    reportId,
    reportedId
  }: {
    reportId: number;
    reportedId: number;
  }) => {
    let updatedPendingReports = this.pendingReports.get();
    const targetedReportedReportsLength =
      updatedPendingReports[`${reportedId}`].data.length;
    if (targetedReportedReportsLength > 1) {
      updatedPendingReports[`${reportedId}`].data = updatedPendingReports[
        `${reportedId}`
      ].data.filter((report) => {
        return report.reportId !== reportId;
      });
    } else {
      delete updatedPendingReports[`${reportedId}`];
    }
    this.setPendingReports(updatedPendingReports);
  };

  removeBannedFromPendingReports = ({ reportedId }: { reportedId: number }) => {
    let updatedPendingReports = this.pendingReports.get();
    delete updatedPendingReports[`${reportedId}`];
    this.setPendingReports(updatedPendingReports);
  };

  decreaseDashboardReportsCount = () => {
    const reportsCount = getDashBoardStore().reportsCount.get();
    if (isPositiveNumber(reportsCount)) {
      getDashBoardStore().reportsCount.set(reportsCount - 1);

      getCoreStorageStore(E_STORAGE_TYPE.SESSION).set(
        E_SESSION_STORAGE_KEY.COUNTS,
        {
          reportsCount: reduce(
            () => getDashBoardStore().reportsCount.get() - 1,
            0
          ),
          reportedCount: reduce(
            () => getDashBoardStore().reportedCount.get(),
            0
          ),
          approvalCount: reduce(
            () => getDashBoardStore().approvalCount.get(),
            0
          ),
          investigationCasesCount: reduce(
            () => getDashBoardStore().investigationCasesCount.get(),
            0
          ),
          activeTime: reduce(() => getDashBoardStore().activeTime.get(), 0)
        }
      );
    }
  };

  decreaseDashboardReportedCount = (reportedId: number) => {
    const reportsData = getReportsStore().pendingReports.get();
    const hasMoreReportedInPartition = !isNullOrUndefined(
      reportsData[reportedId]
    );
    const reportedCount = getDashBoardStore().reportedCount.get();
    const shouldDeacreaseCount =
      !hasMoreReportedInPartition && isPositiveNumber(reportedCount);

    if (shouldDeacreaseCount) {
      getDashBoardStore().reportedCount.set(reportedCount - 1);

      getCoreStorageStore(E_STORAGE_TYPE.SESSION).set(
        E_SESSION_STORAGE_KEY.COUNTS,
        {
          reportsCount: reduce(() => getDashBoardStore().reportsCount.get(), 0),
          reportedCount: reduce(
            () => getDashBoardStore().reportedCount.get() - 1,
            0
          ),
          approvalCount: reduce(
            () => getDashBoardStore().approvalCount.get(),
            0
          ),
          investigationCasesCount: reduce(
            () => getDashBoardStore().investigationCasesCount.get(),
            0
          ),
          activeTime: reduce(() => getDashBoardStore().activeTime.get(), 0)
        }
      );
    }
  };

  resolveReport = async (
    reportId: number,
    reportedId: number,
    type: "resolve" | "email"
  ) => {
    await resolveReportOnDatabase({
      type,
      reportId,
      reportedId
    });
    return;
  };

  banReport = async (
    reportId: number,
    reportedId: number,
    reportedUUID: string,
    type: string,
    extraData: TStatusExtraData,
    pageName: TCorePage
  ) => {
    return await banReportOnDatabase(
      reportId,
      reportedId,
      reportedUUID,
      type,
      extraData,
      pageName
    );
  };

  checkURLSearch = async (search: string) => {
    if (isDefinedString(search)) {
      const searchID = queryString.parse(search).id;
      const searchUserID = queryString.parse(search).userID;
      if (
        isDefinedString(searchID) &&
        isPositiveNumber(parseInt(searchID, 10))
      ) {
        const searchIDNumber = parseInt(searchID, 10);
        await this.getReportByID(searchIDNumber);
      } else if (
        isDefinedString(searchUserID) &&
        isPositiveNumber(parseInt(searchUserID, 10))
      ) {
        await this.getReports(true);
      }
    }
  };

  warnReport = async (
    reportId: number,
    reportedUUID: string,
    reportedId: number,
    TBW_ids: number[],
    pageName: TCorePage
  ) => {
    return await warnReportOnDatabase(
      reportId,
      reportedUUID,
      reportedId,
      TBW_ids,
      pageName
    );
  };
  cleanReportsPool = async () => {
    await getCleanPoolStore().cleanPool({
      currentData: this.reportsByType,
      page: "REPORTS",
      poolType: "REPORT",
      successVoidFunction: () => this.cleanReports()
    });
  };
}

const getReportsStore = memoize(
  () => new ReportsStore(),
  () => 1
);
export default getReportsStore;
